import React from "react";
import { BASE_URL } from "../../../static/constants";
import getToken from "../../../utils/getToken";

// Material Imports
import Button from "@mui/material/Button";
import PersonIcon from "@mui/icons-material/Person";
import TextField from "@mui/material/TextField";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

// Import CSS
import "./index.css";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { showErrorToast, showSuccessToast } from "utils/toast";
import ModalContainer from "components/common/modals/modalContainer/ModalContainer";
import { Formik } from "formik";
import { resetPasswordValidationSchema } from "schemas/resetPasswordValidationSchema";
import SubmitButton from "components/Button/SubmitButton";

export default function ResetPasswordModal(props) {
  const {
    resetPasswordModalOpen,
    closeResetPasswordModal,
    selectedUser,
    title,
  } = props;

  const [processing, setProcessing] = React.useState(false);
  const [passwordVisible, setPasswordVisible] = React.useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] =
    React.useState(false);

  const onClose = () => {
    setProcessing(false);
    closeResetPasswordModal();
    setConfirmPasswordVisible(false);
  };

  const togglePasswordVisible = () => {
    setPasswordVisible(!passwordVisible);
  };

  const toggleConfirmPasswordVisible = () => {
    setConfirmPasswordVisible(!confirmPasswordVisible);
  };

  const saveUserPassword = (values) => {
    const payload = {
      _id: selectedUser.id,
      password: values?.password,
    };

    fetch(`${BASE_URL}/admin/user/password`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        if (data.error) {
          throw Error(
            "Password must contain at least 12 characters including one number and one special character"
          );
        }
        showSuccessToast("Password Updated");
        onClose();
      })
      .catch((error) => {
        const errorStr = error.toString();
        if (errorStr === "Error: jwt malformed") {
          this.props.navigate("/login");
        }
        if (errorStr === "Error: jwt expired") {
          this.props.navigate("/login");
        }
        if (errorStr === "Error: Unauthorized") {
          this.props.navigate("/prohibited");
        }

        showErrorToast(errorStr);
      });
  };

  return (
    <ModalContainer
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
      show={resetPasswordModalOpen}
      onClose={closeResetPasswordModal}
      title={
        <div style={{ display: "flex", alignItems: "center" }}>
          <PersonIcon style={{ color: "#919db2", marginRight: "10px" }} />{" "}
          {title}
        </div>
      }
    >
      <Formik
        initialValues={{}}
        validationSchema={resetPasswordValidationSchema}
        onSubmit={(values) => saveUserPassword(values)}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          isValid,
          handleSubmit,
          setFieldValue,
        }) => {
          return (
            <>
              <div className="reset-password-modal__text">
                {!processing && (
                  <>
                    <div className="reset-password-modal__input">
                      <TextField
                        fullWidth
                        variant="outlined"
                        id="password"
                        name="password"
                        type={passwordVisible ? "text" : "password"}
                        label="Password"
                        placeholder="Password must be at least 12 characters long."
                        error={Boolean(errors.password)}
                        helperText={errors.password}
                        onChange={(e) =>
                          setFieldValue("password", e.target.value)
                        }
                        InputProps={{
                          endAdornment: passwordVisible ? (
                            <VisibilityOffIcon
                              onClick={togglePasswordVisible}
                              style={{
                                position: "absolute",
                                top: 10,
                                right: 10,
                                cursor: "pointer",
                              }}
                            />
                          ) : (
                            <VisibilityIcon
                              onClick={togglePasswordVisible}
                              style={{
                                position: "absolute",
                                top: 10,
                                right: 10,
                                cursor: "pointer",
                              }}
                            />
                          ),
                          error: Boolean(errors.password),
                        }}
                      />
                    </div>
                    <div className="reset-password-modal__input">
                      <TextField
                        fullWidth
                        variant="outlined"
                        id="passwordConfirm"
                        name="passwordConfirm"
                        onChange={(e) =>
                          setFieldValue("passwordConfirm", e.target.value)
                        }
                        type={confirmPasswordVisible ? "text" : "password"}
                        label="Confirm Password"
                        placeholder="Password must be at least 12 characters long."
                        error={Boolean(errors.passwordConfirm)}
                        helperText={errors.passwordConfirm}
                        InputProps={{
                          endAdornment: confirmPasswordVisible ? (
                            <VisibilityOffIcon
                              onClick={toggleConfirmPasswordVisible}
                              style={{
                                position: "absolute",
                                top: 10,
                                right: 10,
                                cursor: "pointer",
                              }}
                            />
                          ) : (
                            <VisibilityIcon
                              onClick={toggleConfirmPasswordVisible}
                              style={{
                                position: "absolute",
                                top: 10,
                                right: 10,
                                cursor: "pointer",
                              }}
                            />
                          ),
                        }}
                      />
                    </div>
                  </>
                )}
                {processing && (
                  <div className="reset-password-modal__input">
                    <div className="reset-password-modal__processing">
                      Processing user password
                    </div>
                    <div className="reset-password-modal__spinner">
                      <LoadingSpinner color="secondary" />
                    </div>
                  </div>
                )}
              </div>
              <div className="d-flex flex-end g-1">
                <SubmitButton
                  handleClick={handleSubmit}
                  variant="contained"
                  color="secondary"
                  isLoading={processing}
                  disabled={processing || !isValid}
                />
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={closeResetPasswordModal}
                  disabled={processing}
                >
                  Cancel
                </Button>
              </div>
            </>
          );
        }}
      </Formik>
    </ModalContainer>
  );
}
